import { useEffect, useRef, useState } from 'react';
import { FractalCanvas } from './FractalCanvas';
import { fireColor } from './firePalette';
import { loadDeepZoom } from './deepZoomLoader';
import { isMobileViewport } from './fullscreenControl';

let sharedObserverCtor = null;
let sharedObserver = null;
const subsectionCallbacks = new Map();
let pagePrewarmed = false;

function schedulePageDeepZoomPrewarm() {
  if (pagePrewarmed || globalThis.__PRERENDER__ || globalThis.IS_REACT_ACT_ENVIRONMENT
    || typeof Worker !== 'function' || Worker?.mock) return;
  pagePrewarmed = true;
  const mobile = isMobileViewport();
  const timer = setTimeout(() => {
    loadDeepZoom().catch(() => {});
    import('./createDeepZoomWorker').then(m => m.prewarmDeepZoomWorkers?.(mobile ? 1 : 2)).catch(() => {});
    if (mobile) {
      import('./deepZoomHelperBudget').then(m => m.prewarmSingleDeepHelper?.()).catch(() => {});
    } else {
      import('./deepZoomMultiHelperPool').then(m => m.prewarmMultiDeepHelpers?.()).catch(() => {});
    }
  }, 20);
  timer?.unref?.();
}

function observeSubsection(element, onVisible) {
  if (typeof IntersectionObserver === 'undefined' || !element) {
    onVisible();
    return () => {};
  }
  if (sharedObserverCtor !== IntersectionObserver || !sharedObserver) {
    sharedObserver?.disconnect?.();
    sharedObserverCtor = IntersectionObserver;
    subsectionCallbacks.clear();
    const mobile = isMobileViewport();
    const rootMargin = mobile
      ? '250px 0px 250px 0px'
      : (typeof window !== 'undefined' && window.innerHeight >= 700 ? '300px 0px 300px 0px' : '200px');
    sharedObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (entry.target && subsectionCallbacks.has(entry.target)) {
          const cb = subsectionCallbacks.get(entry.target);
          cb();
        } else if (!entry.target) {
          for (const cb of [...subsectionCallbacks.values()]) cb();
        }
      }
    }, { rootMargin });
  }
  let active = true;
  const cleanup = () => {
    if (!active) return;
    active = false;
    subsectionCallbacks.delete(element);
    if (subsectionCallbacks.size === 0) {
      sharedObserver?.disconnect?.();
      sharedObserver = null;
    } else {
      sharedObserver?.unobserve?.(element);
    }
  };
  subsectionCallbacks.set(element, () => {
    cleanup();
    onVisible();
  });
  sharedObserver.observe(element);
  return cleanup;
}

function formatSpanLabel(spanX) {
  const num = Number(spanX);
  if (Number.isFinite(num) && num > 0) {
    return `span ${num.toExponential(2)}`;
  }
  const match = typeof spanX === 'string'
    && spanX.trim().match(/^([+-]?(?:\d+(?:\.\d*)?|\.\d+))(?:e([+-]?\d+))?$/i);
  if (match) {
    const mantissa = Number(match[1]);
    const exp = Number(match[2] ?? 0);
    if (Number.isFinite(mantissa) && mantissa > 0 && Number.isFinite(exp)) {
      const normExp = Math.floor(Math.log10(mantissa));
      const scaled = mantissa / (10 ** normExp);
      const totalExp = exp + normExp;
      return `span ${scaled.toFixed(2)}e${totalExp >= 0 ? `+${totalExp}` : totalExp}`;
    }
  }
  return 'Deep zoom';
}

/** Mount each precise camera only when its subsection approaches the viewport. */
export function FractalSubsection({ preset }) {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const { id, title, description, initialView, colorRange,
    maxRenderPixels, samplesPerPixel, width = 1200, height = 900 } = preset;
  const fallbackSrc = preset.fallbackSrc ?? `./images/${id}.webp`;
  const fallbackAlt = preset.fallbackAlt ?? `${title} — ${description}`;

  useEffect(() => {
    if (globalThis.__PRERENDER__) return undefined;
    schedulePageDeepZoomPrewarm();
    return observeSubsection(sectionRef.current, () => setVisible(true));
  }, []);

  // These Number ranges are only the loading placeholder. The interactive
  // camera always starts from the full-precision string coordinates above.
  const range = (center, span) => [Number(center) - Number(span) / 2, Number(center) + Number(span) / 2];

  return <section ref={sectionRef} id={id} className="fractal-subsection" aria-labelledby={`${id}-title`}>
    <h4 id={`${id}-title`}>{title}</h4>
    <p>{description}</p>
    {visible ? <FractalCanvas
      width={width}
      height={height}
      xRange={range(initialView.centerX, initialView.spanX)}
      yRange={range(initialView.centerY, initialView.spanY)}
      initialView={initialView}
      deepColorRange={colorRange}
      maxRenderPixels={maxRenderPixels}
      samplesPerPixel={samplesPerPixel}
      colorFunc={fireColor}
      preferredRenderer="canvas"
      fallbackSrc={fallbackSrc}
      fallbackAlt={fallbackAlt}
    /> : <figure>
      <div className="image-container deep-zoom-container" data-fullscreen="false" data-zen-mode="false">
        <div
          className="fractal-subsection-placeholder"
          style={{
            width,
            maxWidth: '100%',
            height: 'auto',
            aspectRatio: `${width} / ${height}`,
          }}
        >
          <img
            loading="lazy"
            decoding="async"
            src={fallbackSrc}
            alt={fallbackAlt}
            width={width}
            height={height}
          />
        </div>
        <div className="deep-zoom-controls" aria-hidden="true" data-nosnippet="">
          <button type="button" disabled tabIndex={-1}>Undo</button>
          <button type="button" disabled tabIndex={-1}>Reset</button>
        </div>
        <div className="deep-zoom-status" aria-hidden="true" data-nosnippet="">
          <span>{formatSpanLabel(initialView?.spanX)}</span>
          <span>Rendering…</span>
        </div>
      </div>
    </figure>}
  </section>;
}
